import { z } from "zod";
import {
  VendorCreateSchema,
  VendorUpdateSchema,
  VendorResponseSchema,
  CategoryCreateSchema,
  CategoryUpdateSchema,
  CategoryResponseSchema,
  ProductCreateSchema,
  ProductUpdateSchema,
  ProductResponseSchema,
  ProductDimensionsSchema,
  ProductListQuerySchema,
  // Product Variant Schemas (BYK-75)
  ProductMaterialSchema,
  SurfaceFinishSchema,
  ProductColorSchema,
  ProductVariantCreateSchema,
  ProductVariantUpdateSchema,
  ProductVariantResponseSchema,
  ProductWithVariantsResponseSchema,
  ProductSemanticsSchema,
  Product3DMetadataSchema,
  // BYK-77: Cabinet Modules
  ModuleTypeSchema,
  CabinetInternalConfigSchema,
  HandleConfigSchema,
  SnapPointSchema,
  CabinetModuleCreateSchema,
  CabinetModuleUpdateSchema,
  CabinetModuleResponseSchema,
  // Handle Products
  HandleTypeSchema,
  HandleFinishOptionSchema,
  HandleProductCreateSchema,
  HandleProductUpdateSchema,
  HandleProductResponseSchema,
  // File Upload Schemas
  PresignRequestSchema,
  PresignResponseSchema,
  UploadCompleteSchema,
  FileResponseSchema,
  ErrorResponseSchema,
  SessionResponseSchema,
  // BYK-6: AI Render Schemas
  RenderStyleSchema,
  RenderCameraSchema,
  RenderLightingSchema,
  RenderStatusSchema,
  RenderCreateSchema,
  RenderMetadataSchema,
  RenderResponseSchema,
  RenderProgressSchema,
  // BYK-141: Multi-Turn Refinement Schemas
  RenderRefineCreateSchema,
  RenderWithRefinementsResponseSchema,
  RenderLineageResponseSchema,
  MAX_REFINEMENT_DEPTH,
} from "../schemas/index.js";

// =============================================================================
// Vendor Types
// =============================================================================

export type VendorCreate = z.infer<typeof VendorCreateSchema>;
export type VendorUpdate = z.infer<typeof VendorUpdateSchema>;
export type VendorResponse = z.infer<typeof VendorResponseSchema>;

// =============================================================================
// Category Types
// =============================================================================

export type CategoryCreate = z.infer<typeof CategoryCreateSchema>;
export type CategoryUpdate = z.infer<typeof CategoryUpdateSchema>;
export type CategoryResponse = z.infer<typeof CategoryResponseSchema>;

// =============================================================================
// Product Types
// =============================================================================

export type ProductDimensions = z.infer<typeof ProductDimensionsSchema>;
export type ProductCreate = z.infer<typeof ProductCreateSchema>;
export type ProductUpdate = z.infer<typeof ProductUpdateSchema>;
export type ProductResponse = z.infer<typeof ProductResponseSchema>;
export type ProductListQuery = z.infer<typeof ProductListQuerySchema>;

// =============================================================================
// Product Variant Types (BYK-75: Catalog Schema)
// =============================================================================

export type ProductMaterial = z.infer<typeof ProductMaterialSchema>;
export type SurfaceFinish = z.infer<typeof SurfaceFinishSchema>;
export type ProductColor = z.infer<typeof ProductColorSchema>;
export type ProductVariantCreate = z.infer<typeof ProductVariantCreateSchema>;
export type ProductVariantUpdate = z.infer<typeof ProductVariantUpdateSchema>;
export type ProductVariantResponse = z.infer<typeof ProductVariantResponseSchema>;
export type ProductWithVariantsResponse = z.infer<typeof ProductWithVariantsResponseSchema>;
export type ProductSemantics = z.infer<typeof ProductSemanticsSchema>;
export type Product3DMetadata = z.infer<typeof Product3DMetadataSchema>;

// =============================================================================
// BYK-77: Cabinet Module Types
// =============================================================================

export type ModuleType = z.infer<typeof ModuleTypeSchema>;
export type CabinetInternalConfig = z.infer<typeof CabinetInternalConfigSchema>;
export type HandleConfig = z.infer<typeof HandleConfigSchema>;
export type SnapPoint = z.infer<typeof SnapPointSchema>;
export type CabinetModuleCreate = z.infer<typeof CabinetModuleCreateSchema>;
export type CabinetModuleUpdate = z.infer<typeof CabinetModuleUpdateSchema>;
export type CabinetModuleResponse = z.infer<typeof CabinetModuleResponseSchema>;

// =============================================================================
// Handle Product Types
// =============================================================================

export type HandleType = z.infer<typeof HandleTypeSchema>;
export type HandleFinishOption = z.infer<typeof HandleFinishOptionSchema>;
export type HandleProductCreate = z.infer<typeof HandleProductCreateSchema>;
export type HandleProductUpdate = z.infer<typeof HandleProductUpdateSchema>;
export type HandleProductResponse = z.infer<typeof HandleProductResponseSchema>;

// =============================================================================
// File Upload Types
// =============================================================================

export type PresignRequest = z.infer<typeof PresignRequestSchema>;
export type PresignResponse = z.infer<typeof PresignResponseSchema>;
export type UploadComplete = z.infer<typeof UploadCompleteSchema>;
export type FileResponse = z.infer<typeof FileResponseSchema>;

// =============================================================================
// API Response Types
// =============================================================================

export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;
export type SessionResponse = z.infer<typeof SessionResponseSchema>;

export type PaginatedResponse<T> = {
  items: T[];
  total: number;
  limit: number;
  offset: number;
};

// =============================================================================
// Domain Entities (Kitchen Design)
// =============================================================================

export type VendorName = "Niga" | "Teka" | "Neolith";

export type CategoryName =
  | "Sinks"
  | "Taps"
  | "Purifiers"
  | "Ovens"
  | "Cooktops"
  | "Surfaces"
  | "Cabinets";

export interface RaumplanVolume {
  x: number;
  y: number;
  z: number;
  width: number;
  depth: number;
  height: number;
  type: "cabinet" | "appliance" | "surface" | "sink" | "fixture";
  productId?: string;
}

export interface KitchenMeasurements {
  roomWidth: number;
  roomDepth: number;
  ceilingHeight: number;
  windowPositions?: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
  doorPositions?: Array<{
    x: number;
    width: number;
  }>;
}

export interface DesignRequirements {
  familySize: number;
  isReligious: boolean;
  hostingCapacity?: number;
  preferredVendors?: VendorName[];
  budgetRange?: {
    min: number;
    max: number;
  };
}

// =============================================================================
// Floor Plan Types (2D + 3D Hybrid)
// =============================================================================

export interface Point2D {
  x: number; // cm from origin
  y: number; // cm from origin
}

export interface Point3D {
  x: number; // cm
  y: number; // cm (depth in 2D terms)
  z: number; // cm (height)
}

export type WallSide = "north" | "east" | "south" | "west";

export interface WallSegment {
  id: string;
  start: Point2D;
  end: Point2D;
  thickness: number; // cm (default 15)
  height: number; // cm (default room height)
}

export type StructuralElementType =
  | "window"
  | "door"
  | "door_double"
  | "port_water"
  | "port_electric"
  | "port_gas";

export interface StructuralElement {
  id: string;
  type: StructuralElementType;
  wallId: string; // Which wall segment it's on
  offset: number; // Distance along wall from start point (cm)
  width: number; // cm
  height: number; // cm
  elevation: number; // Height from floor (cm), 0 for doors
}

export type KitchenZone = "prep" | "cooking" | "cleaning" | "storage";

/**
 * Semantic metadata for AI awareness and conflict detection.
 * These properties describe the functional characteristics of a placed volume.
 */
export interface VolumeSemantics {
  /** True for sinks, dishwashers - affects work triangle calculation */
  isWaterSource: boolean;
  /** True for ovens, cooktops - affects work triangle and safety rules */
  isHeatSource: boolean;
  /** True for fridges, freezers - affects work triangle calculation */
  isColdStorage: boolean;
  /** True for dishwashers, ovens - needs door swing clearance */
  requiresFrontAccess: boolean;
  /** cm required in front for door swing (usually 60-90) */
  frontAccessDepth: number;
  /** True for cooktops - needs ventilation hood above */
  requiresVentilation: boolean;
  /** True if can be placed on counter (sinks, cooktops) */
  isCounterMountable: boolean;
  /** True for fridges, tall cabinets - standalone floor items */
  isFloorStanding: boolean;
}

/**
 * Default semantic values - all false/zero for unknown products
 */
export const DEFAULT_VOLUME_SEMANTICS: VolumeSemantics = {
  isWaterSource: false,
  isHeatSource: false,
  isColdStorage: false,
  requiresFrontAccess: false,
  frontAccessDepth: 0,
  requiresVentilation: false,
  isCounterMountable: false,
  isFloorStanding: false,
};

export interface PlacedVolume {
  id: string;
  productId: string;
  position: Point3D;
  rotation: number; // degrees (Y-axis rotation)
  zone: KitchenZone;
  isKashrutDistinct?: boolean; // For kosher kitchens: meat vs dairy
  /** Semantic metadata for AI awareness - auto-populated from product category */
  semantics?: VolumeSemantics;
}

// =============================================================================
// BYK-31: Cabinet Hardware Animation Types
// =============================================================================

/**
 * Types of animated cabinet components
 */
export type CabinetPartType = "door_left" | "door_right" | "drawer" | "door_single";

/**
 * Animation state for a single cabinet part (door or drawer)
 */
export interface CabinetPartState {
  /** Type of part */
  type: CabinetPartType;
  /** Current open percentage (0 = closed, 1 = fully open) */
  openAmount: number;
  /** Whether animation is currently playing */
  isAnimating: boolean;
}

/**
 * Animation state for a cabinet volume
 */
export interface CabinetAnimationState {
  /** Volume ID this animation state belongs to */
  volumeId: string;
  /** State of each animated part (keyed by part name: "door_left", "door_right", "drawer_1", etc.) */
  parts: Record<string, CabinetPartState>;
}

/**
 * Configuration for cabinet animations
 */
export interface CabinetAnimationConfig {
  /** Door swing angle in degrees (default: 110) */
  doorSwingAngle: number;
  /** Drawer slide distance in cm (default: 40) */
  drawerSlideDistance: number;
  /** Animation duration in ms (default: 300) */
  animationDuration: number;
  /** Easing function type */
  easingType: "linear" | "ease-in" | "ease-out" | "ease-in-out";
}

/**
 * Default animation configuration
 */
export const DEFAULT_CABINET_ANIMATION_CONFIG: CabinetAnimationConfig = {
  doorSwingAngle: 110,
  drawerSlideDistance: 40,
  animationDuration: 300,
  easingType: "ease-in-out",
};

/**
 * Cabinet layout type - determines which parts are rendered
 */
export type CabinetLayoutType =
  | "single_door"    // One door (small cabinets)
  | "double_door"    // Two doors opening outward
  | "single_drawer"  // One drawer
  | "multi_drawer"   // Multiple drawers stacked
  | "door_drawer"    // Door on top, drawer(s) below
  | "open_shelf";    // No doors, open shelving

/**
 * Infer cabinet layout from product dimensions and type
 */
export function inferCabinetLayout(
  width: number,
  height: number,
  type: string
): CabinetLayoutType {
  // Drawers typically in base cabinets < 50cm height for drawer section
  if (type.toLowerCase().includes("drawer")) {
    return height > 60 ? "multi_drawer" : "single_drawer";
  }
  // Wide cabinets (> 50cm) typically have double doors
  if (width > 50) {
    return "double_door";
  }
  // Default to single door for standard base/wall cabinets
  return "single_door";
}

export interface RoomConfig {
  width: number; // cm
  depth: number; // cm
  height: number; // cm (ceiling height)
}

export interface FloorPlanState {
  room: RoomConfig;
  walls: WallSegment[];
  elements: StructuralElement[];
  volumes: PlacedVolume[];
}

export type EditorTool =
  | "select"
  | "wall"
  | "window"
  | "door"
  | "measure"
  | "place"
  | "move"
  | "rotate";

export type ViewMode = "2d" | "3d" | "split";

// =============================================================================
// Occupancy Grid Types (Spatial Awareness)
// =============================================================================

export type OccupantType = "wall" | "volume" | "element" | "access_zone";

/**
 * Represents a single cell in the occupancy grid.
 * Used for collision detection and AI placement calculations.
 */
export interface OccupancyCell {
  /** Whether this cell is occupied by any object */
  occupied: boolean;
  /** ID of the object occupying this cell (null if empty) */
  occupantId: string | null;
  /** Type of object occupying this cell */
  occupantType: OccupantType | null;
  /** Whether this cell is part of an accessible path (not blocked) */
  isAccessiblePath: boolean;
  /** Distance to nearest wall in cm (for ventilation/clearance calculations) */
  distanceToWall: number;
  /** If this is an access zone, which volume requires it */
  accessZoneForId?: string;
}

/**
 * Default empty cell values
 */
export const DEFAULT_OCCUPANCY_CELL: OccupancyCell = {
  occupied: false,
  occupantId: null,
  occupantType: null,
  isAccessiblePath: true,
  distanceToWall: Infinity,
};

/**
 * Result of a placement validation check
 */
export interface PlacementValidation {
  valid: boolean;
  reason?: string;
  conflictingCells?: Array<{ x: number; y: number }>;
  conflictingObjectIds?: string[];
}

// =============================================================================
// AI Generation Types
// =============================================================================

export interface AILayoutRequest {
  room: RoomConfig;
  requirements: DesignRequirements;
  existingElements: StructuralElement[];
  availableProducts: ProductResponse[];
}

export interface AILayoutSuggestion {
  volumes: PlacedVolume[];
  reasoning: string;
  kashrutCompliant?: boolean;
}

// =============================================================================
// Work Triangle & Layout Suggestion Types
// =============================================================================

/**
 * Kitchen layout patterns
 */
export type LayoutType = "L-shape" | "U-shape" | "Galley" | "Island" | "Single-wall";

/**
 * Work triangle point types (Sink ↔ Fridge ↔ Stove)
 */
export type WorkTrianglePoint = "sink" | "fridge" | "cooktop";

/**
 * A single placement in a layout suggestion
 */
export interface LayoutPlacement {
  /** Which work triangle point this represents */
  role: WorkTrianglePoint;
  /** Product ID to place */
  productId: string;
  /** Position in room coordinates (cm) */
  position: Point3D;
  /** Rotation in degrees */
  rotation: number;
}

/**
 * Work triangle measurements and validation
 * Optimal: perimeter 4-8m, each leg 1.2-2.7m
 */
export interface WorkTriangleMetrics {
  /** Total perimeter in cm */
  perimeter: number;
  /** Distance sink to fridge in cm */
  sinkToFridge: number;
  /** Distance fridge to cooktop in cm */
  fridgeToCooktop: number;
  /** Distance cooktop to sink in cm */
  cooktopToSink: number;
  /** Whether triangle meets optimal criteria */
  isOptimal: boolean;
  /** Reason if not optimal */
  issues?: string[];
}

/**
 * A single layout suggestion from the AI/fallback engine
 */
export interface LayoutSuggestion {
  /** Unique ID for this suggestion */
  id: string;
  /** Layout pattern type */
  type: LayoutType;
  /** Quality score 0-1 (higher is better) */
  score: number;
  /** The three work triangle placements */
  placements: LayoutPlacement[];
  /** Work triangle measurements */
  triangle: WorkTriangleMetrics;
  /** Human-readable reasoning */
  reasoning: string;
  /** Whether this came from AI or fallback rules */
  source: "ai" | "fallback";
}

/**
 * Request body for layout suggestion API
 */
export interface LayoutSuggestRequest {
  room: RoomConfig;
  walls: WallSegment[];
  elements: StructuralElement[];
  /** Existing volumes to work around */
  existingVolumes?: PlacedVolume[];
}

/**
 * Response from layout suggestion API
 */
export interface LayoutSuggestResponse {
  /** Multiple layout options, sorted by score descending */
  layouts: LayoutSuggestion[];
  /** Whether AI was available or fallback was used */
  usedFallback: boolean;
}

/**
 * Work triangle optimal ranges (in cm)
 */
export const WORK_TRIANGLE_OPTIMAL = {
  /** Minimum perimeter (400cm = 4m) */
  minPerimeter: 400,
  /** Maximum perimeter (800cm = 8m) */
  maxPerimeter: 800,
  /** Minimum leg length (120cm = 1.2m) */
  minLeg: 120,
  /** Maximum leg length (270cm = 2.7m) */
  maxLeg: 270,
} as const;

// =============================================================================
// BYK-17: Style Transfer - Material Palette Types
// =============================================================================

/**
 * Style palette preset names
 */
export type StylePresetName =
  | "warm-2025"
  | "nordic"
  | "mediterranean"
  | "industrial"
  | "classic";

/**
 * PBR Material configuration for Babylon.js
 */
export interface MaterialConfig {
  /** Base color (hex or CSS color) */
  baseColor: string;
  /** Metallic value 0-1 */
  metallic: number;
  /** Roughness value 0-1 */
  roughness: number;
  /** Optional emissive color for accent lighting */
  emissiveColor?: string;
  /** Optional ambient occlusion intensity */
  ambientOcclusionIntensity?: number;
}

/**
 * Surface types that can have materials applied
 */
export type SurfaceType =
  | "countertop"
  | "cabinet-door"
  | "cabinet-body"
  | "backsplash"
  | "flooring"
  | "wall"
  | "appliance"
  | "hardware";

/**
 * A complete style palette with materials for all surface types
 */
export interface StylePalette {
  /** Unique identifier */
  id: string;
  /** Display name */
  name: string;
  /** Optional description */
  description?: string;
  /** Whether this is a built-in preset */
  isPreset: boolean;
  /** Preset name if this is a preset */
  presetName?: StylePresetName;
  /** Materials for each surface type */
  materials: Record<SurfaceType, MaterialConfig>;
  /** Accent color for UI elements */
  accentColor: string;
  /** Preview thumbnail URL (optional) */
  thumbnailUrl?: string;
}

/**
 * Request to generate a custom palette from AI description
 */
export interface CustomPaletteRequest {
  /** Natural language description (e.g., "warm earthy tones with brass accents") */
  description: string;
  /** Base preset to modify (optional) */
  basePreset?: StylePresetName;
}

/**
 * Built-in style palette presets
 */
export const STYLE_PALETTE_PRESETS: Record<StylePresetName, Omit<StylePalette, "id">> = {
  "warm-2025": {
    name: "Warm 2025",
    description: "Earthy tones with terracotta accents - 2025 trend",
    isPreset: true,
    presetName: "warm-2025",
    accentColor: "#c77b4a",
    materials: {
      countertop: { baseColor: "#d4c4b5", metallic: 0.1, roughness: 0.4 },
      "cabinet-door": { baseColor: "#8b9a7b", metallic: 0, roughness: 0.6 },
      "cabinet-body": { baseColor: "#f5f0eb", metallic: 0, roughness: 0.7 },
      backsplash: { baseColor: "#c77b4a", metallic: 0.05, roughness: 0.3 },
      flooring: { baseColor: "#b5a99a", metallic: 0, roughness: 0.8 },
      wall: { baseColor: "#faf8f5", metallic: 0, roughness: 0.9 },
      appliance: { baseColor: "#2d2d2d", metallic: 0.8, roughness: 0.2 },
      hardware: { baseColor: "#b08d57", metallic: 0.9, roughness: 0.3 },
    },
  },
  nordic: {
    name: "Nordic Minimalist",
    description: "Clean whites and light wood with black accents",
    isPreset: true,
    presetName: "nordic",
    accentColor: "#1a1a1a",
    materials: {
      countertop: { baseColor: "#ffffff", metallic: 0.05, roughness: 0.3 },
      "cabinet-door": { baseColor: "#e8dfd4", metallic: 0, roughness: 0.5 },
      "cabinet-body": { baseColor: "#ffffff", metallic: 0, roughness: 0.6 },
      backsplash: { baseColor: "#f5f5f5", metallic: 0, roughness: 0.4 },
      flooring: { baseColor: "#d4c9bc", metallic: 0, roughness: 0.7 },
      wall: { baseColor: "#ffffff", metallic: 0, roughness: 0.9 },
      appliance: { baseColor: "#1a1a1a", metallic: 0.7, roughness: 0.3 },
      hardware: { baseColor: "#1a1a1a", metallic: 0.6, roughness: 0.4 },
    },
  },
  mediterranean: {
    name: "Mediterranean",
    description: "Blue tiles, warm terracotta, and rustic wood",
    isPreset: true,
    presetName: "mediterranean",
    accentColor: "#1e5f8a",
    materials: {
      countertop: { baseColor: "#e8dfd4", metallic: 0.1, roughness: 0.5 },
      "cabinet-door": { baseColor: "#6b8e7a", metallic: 0, roughness: 0.6 },
      "cabinet-body": { baseColor: "#f0e6d8", metallic: 0, roughness: 0.7 },
      backsplash: { baseColor: "#1e5f8a", metallic: 0.1, roughness: 0.3 },
      flooring: { baseColor: "#c77b4a", metallic: 0, roughness: 0.8 },
      wall: { baseColor: "#fdf8f3", metallic: 0, roughness: 0.9 },
      appliance: { baseColor: "#f5f5f5", metallic: 0.6, roughness: 0.3 },
      hardware: { baseColor: "#8b6914", metallic: 0.85, roughness: 0.35 },
    },
  },
  industrial: {
    name: "Industrial",
    description: "Raw concrete, steel, and dark wood",
    isPreset: true,
    presetName: "industrial",
    accentColor: "#4a4a4a",
    materials: {
      countertop: { baseColor: "#8a8a8a", metallic: 0.3, roughness: 0.6 },
      "cabinet-door": { baseColor: "#3d3d3d", metallic: 0.1, roughness: 0.5 },
      "cabinet-body": { baseColor: "#4a4a4a", metallic: 0.05, roughness: 0.6 },
      backsplash: { baseColor: "#6b6b6b", metallic: 0.2, roughness: 0.7 },
      flooring: { baseColor: "#5c5c5c", metallic: 0.1, roughness: 0.8 },
      wall: { baseColor: "#b5b5b5", metallic: 0, roughness: 0.9 },
      appliance: { baseColor: "#2d2d2d", metallic: 0.85, roughness: 0.15 },
      hardware: { baseColor: "#1a1a1a", metallic: 0.9, roughness: 0.2 },
    },
  },
  classic: {
    name: "Classic Elegance",
    description: "Timeless cream and wood with gold accents",
    isPreset: true,
    presetName: "classic",
    accentColor: "#c9a962",
    materials: {
      countertop: { baseColor: "#f5f0e8", metallic: 0.15, roughness: 0.35 },
      "cabinet-door": { baseColor: "#f8f4ef", metallic: 0, roughness: 0.5 },
      "cabinet-body": { baseColor: "#fffcf7", metallic: 0, roughness: 0.6 },
      backsplash: { baseColor: "#e8e0d5", metallic: 0.05, roughness: 0.4 },
      flooring: { baseColor: "#8b7355", metallic: 0, roughness: 0.7 },
      wall: { baseColor: "#fdfbf7", metallic: 0, roughness: 0.9 },
      appliance: { baseColor: "#f5f5f5", metallic: 0.5, roughness: 0.35 },
      hardware: { baseColor: "#c9a962", metallic: 0.95, roughness: 0.25 },
    },
  },
};

// =============================================================================
// BYK-22: Conflict Highlighting Types
// =============================================================================

/**
 * Types of conflicts that can be detected
 */
export type ConflictType =
  | "overlap" // Two volumes intersecting
  | "wall-collision" // Volume intersects with wall
  | "stove-under-window" // Cooktop/oven placed under window (fire hazard)
  | "blocked-access" // Access zone blocked by another volume
  | "insufficient-clearance" // Not enough space for door swing
  | "kashrut-proximity" // Meat/dairy items too close (< 60cm)
  | "water-near-electric" // Water source too close to electrical
  | "ventilation-blocked"; // Heat source without ventilation clearance

/**
 * Severity levels for conflicts
 */
export type ConflictSeverity =
  | "error" // Must be resolved (overlap, wall collision)
  | "warning" // Should be resolved (safety concerns)
  | "info"; // Informational (best practices)

/**
 * A detected conflict between volumes or with the room
 */
export interface Conflict {
  /** Unique identifier for this conflict */
  id: string;
  /** Type of conflict */
  type: ConflictType;
  /** Severity level */
  severity: ConflictSeverity;
  /** Human-readable message */
  message: string;
  /** Suggestion to resolve the conflict */
  suggestion: string;
  /** IDs of volumes involved in this conflict */
  volumeIds: string[];
  /** Optional: specific position where conflict occurs */
  position?: Point2D;
  /** Optional: area affected by conflict */
  area?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

/**
 * Conflict indicator colors by severity
 */
export const CONFLICT_COLORS: Record<ConflictSeverity, string> = {
  error: "#ef4444", // Red
  warning: "#f59e0b", // Amber
  info: "#3b82f6", // Blue
};

/**
 * Human-readable labels for conflict types
 */
export const CONFLICT_TYPE_LABELS: Record<ConflictType, string> = {
  overlap: "Overlapping Items",
  "wall-collision": "Wall Collision",
  "stove-under-window": "Stove Under Window",
  "blocked-access": "Blocked Access",
  "insufficient-clearance": "Insufficient Clearance",
  "kashrut-proximity": "Kashrut Proximity",
  "water-near-electric": "Water Near Electric",
  "ventilation-blocked": "Ventilation Blocked",
};

/**
 * Conflict detection result
 */
export interface ConflictDetectionResult {
  /** All detected conflicts */
  conflicts: Conflict[];
  /** Number of errors (must fix) */
  errorCount: number;
  /** Number of warnings (should fix) */
  warningCount: number;
  /** Number of info items */
  infoCount: number;
  /** Whether the layout is valid (no errors) */
  isValid: boolean;
}

// =============================================================================
// BYK-32: Enhanced Snap-to-Wall/Grid System Types
// =============================================================================

/**
 * Types of snap targets for visual feedback
 */
export type SnapTargetType =
  | "grid" // Standard grid snapping
  | "wall" // Snap to wall surface
  | "corner" // Snap to room corner
  | "cabinet" // Snap to adjacent cabinet
  | "alignment"; // Snap to align with other objects

/**
 * A detected snap target during drag operation
 */
export interface SnapTarget {
  /** Type of snap target */
  type: SnapTargetType;
  /** Snapped position */
  position: Point2D;
  /** Original position before snap */
  originalPosition: Point2D;
  /** Distance from original to snapped position */
  distance: number;
  /** ID of the target object (wall, cabinet, etc.) */
  targetId?: string;
  /** Which side of the target (for walls: north/east/south/west) */
  side?: WallSide;
  /** Priority (lower = stronger snap) */
  priority: number;
}

/**
 * Visual snap guide line for rendering
 */
export interface SnapGuideLine {
  /** Start point of the guide line */
  start: Point2D;
  /** End point of the guide line */
  end: Point2D;
  /** Type of snap this line represents */
  type: SnapTargetType;
  /** Whether this is the active (winning) snap */
  isActive: boolean;
}

/**
 * Result of snap calculation
 */
export interface SnapResult {
  /** Final snapped position */
  position: Point2D;
  /** Whether any snapping occurred */
  didSnap: boolean;
  /** All potential snap targets found */
  targets: SnapTarget[];
  /** The winning snap target (highest priority that was in range) */
  activeTarget: SnapTarget | null;
  /** Guide lines to render for visual feedback */
  guideLines: SnapGuideLine[];
}

/**
 * Configuration for snap behavior
 */
export interface SnapConfig {
  /** Enable grid snapping */
  gridEnabled: boolean;
  /** Grid size in cm */
  gridSize: number;
  /** Enable wall proximity snapping */
  wallEnabled: boolean;
  /** Distance in cm to trigger wall snap */
  wallThreshold: number;
  /** Enable cabinet-to-cabinet snapping */
  cabinetEnabled: boolean;
  /** Distance in cm to trigger cabinet snap */
  cabinetThreshold: number;
  /** Enable corner snapping */
  cornerEnabled: boolean;
  /** Distance in cm to trigger corner snap */
  cornerThreshold: number;
  /** Enable alignment snapping (align with other objects) */
  alignmentEnabled: boolean;
  /** Distance in cm to trigger alignment snap */
  alignmentThreshold: number;
}

/**
 * Default snap configuration
 */
export const DEFAULT_SNAP_CONFIG: SnapConfig = {
  gridEnabled: true,
  gridSize: 10, // 10cm grid
  wallEnabled: true,
  wallThreshold: 20, // Snap when within 20cm of wall
  cabinetEnabled: true,
  cabinetThreshold: 15, // Snap when within 15cm of another cabinet
  cornerEnabled: true,
  cornerThreshold: 30, // Snap to corners within 30cm
  alignmentEnabled: true,
  alignmentThreshold: 10, // Align when within 10cm of other object edges
};

/**
 * Snap priority values (lower = higher priority)
 */
export const SNAP_PRIORITY = {
  corner: 1, // Corners are highest priority
  wall: 2, // Then walls
  cabinet: 3, // Then adjacent cabinets
  alignment: 4, // Then alignment
  grid: 5, // Grid is lowest priority
} as const;

// =============================================================================
// BYK-36: Dynamic Cabinet Resizing - Parametric Types
// =============================================================================

/**
 * Cabinet front types (door or drawer configurations)
 */
export type CabinetFrontType =
  | "single-door" // Single door
  | "double-door" // Two doors
  | "drawers-only" // All drawers
  | "door-drawer" // Door on top, drawers below
  | "drawer-door"; // Drawer on top, door below

/**
 * Cabinet mounting type
 */
export type CabinetMountType = "base" | "wall" | "tall" | "corner";

/**
 * Hardware style for handles/knobs
 */
export type HardwareStyle = "bar" | "knob" | "cup" | "integrated" | "push-to-open";

/**
 * Parametric cabinet dimensions (all in mm for precision)
 */
export interface CabinetDimensions {
  /** Width in mm (typical: 150-1200mm in 50mm increments) */
  width: number;
  /** Height in mm (base: 720mm, wall: 400-900mm, tall: 2000-2400mm) */
  height: number;
  /** Depth in mm (base: 560-600mm, wall: 300-350mm) */
  depth: number;
}

/**
 * Drawer layout within a cabinet
 */
export interface DrawerLayout {
  /** Whether the calculated layout is valid */
  valid: boolean;
  /** Array of drawer heights in mm (from bottom to top) */
  heights: number[];
  /** Array of Y positions for drawer centers (from bottom) */
  positions: number[];
  /** Error message if layout is invalid */
  error?: string;
}

/**
 * Panel thickness specifications
 */
export interface PanelThickness {
  /** Side panel thickness (typically 18mm) */
  side: number;
  /** Top panel thickness (typically 18mm) */
  top: number;
  /** Bottom panel thickness (typically 18mm) */
  bottom: number;
  /** Back panel thickness (typically 8mm or 18mm) */
  back: number;
  /** Door/drawer front thickness (typically 18-22mm) */
  front: number;
}

/**
 * Complete parametric cabinet configuration
 */
export interface ParametricCabinetConfig {
  /** Unique identifier for this cabinet instance */
  id: string;
  /** Base dimensions */
  dimensions: CabinetDimensions;
  /** Mounting type */
  mountType: CabinetMountType;
  /** Front configuration */
  frontType: CabinetFrontType;
  /** Number of drawers (for drawer-based fronts) */
  drawerCount: number;
  /** Panel thickness configuration */
  panelThickness: PanelThickness;
  /** Hardware style */
  hardwareStyle: HardwareStyle;
  /** Material/finish ID reference */
  materialId?: string;
  /** Whether this is a custom (resized) cabinet */
  isCustom: boolean;
}

/**
 * Dimension constraints for validation
 */
export interface DimensionConstraints {
  /** Minimum width in mm */
  minWidth: number;
  /** Maximum width in mm */
  maxWidth: number;
  /** Minimum height in mm */
  minHeight: number;
  /** Maximum height in mm */
  maxHeight: number;
  /** Minimum depth in mm */
  minDepth: number;
  /** Maximum depth in mm */
  maxDepth: number;
  /** Minimum drawer height in mm (hardware constraint) */
  minDrawerHeight: number;
  /** Standard width increment in mm */
  widthIncrement: number;
}

/**
 * Default dimension constraints by mount type
 */
export const CABINET_CONSTRAINTS: Record<CabinetMountType, DimensionConstraints> = {
  base: {
    minWidth: 150,
    maxWidth: 1200,
    minHeight: 600,
    maxHeight: 900,
    minDepth: 500,
    maxDepth: 700,
    minDrawerHeight: 100,
    widthIncrement: 50,
  },
  wall: {
    minWidth: 150,
    maxWidth: 1200,
    minHeight: 300,
    maxHeight: 900,
    minDepth: 280,
    maxDepth: 400,
    minDrawerHeight: 100,
    widthIncrement: 50,
  },
  tall: {
    minWidth: 300,
    maxWidth: 900,
    minHeight: 1800,
    maxHeight: 2400,
    minDepth: 500,
    maxDepth: 650,
    minDrawerHeight: 100,
    widthIncrement: 50,
  },
  corner: {
    minWidth: 800,
    maxWidth: 1200,
    minHeight: 600,
    maxHeight: 900,
    minDepth: 500,
    maxDepth: 700,
    minDrawerHeight: 100,
    widthIncrement: 50,
  },
};

/**
 * Default panel thickness values (industry standard)
 */
export const DEFAULT_PANEL_THICKNESS: PanelThickness = {
  side: 18,
  top: 18,
  bottom: 18,
  back: 8, // Or 18 for full back panel
  front: 18,
};

/**
 * Default gap/reveal sizes
 */
export const CABINET_GAPS = {
  /** Gap between drawer fronts (reveal) */
  drawerGap: 3,
  /** Gap at top of drawer stack */
  topGap: 3,
  /** Gap at bottom of drawer stack */
  bottomGap: 3,
  /** Gap between door and frame */
  doorGap: 2,
  /** Clearance between drawer box and cabinet */
  drawerSideClearance: 3,
} as const;

// =============================================================================
// BYK-6: AI Render Types
// =============================================================================

export type RenderStyle = z.infer<typeof RenderStyleSchema>;
export type RenderCamera = z.infer<typeof RenderCameraSchema>;
export type RenderLighting = z.infer<typeof RenderLightingSchema>;
export type RenderStatusType = z.infer<typeof RenderStatusSchema>;
export type RenderCreate = z.infer<typeof RenderCreateSchema>;
export type RenderMetadata = z.infer<typeof RenderMetadataSchema>;
export type RenderResponse = z.infer<typeof RenderResponseSchema>;
export type RenderProgressData = z.infer<typeof RenderProgressSchema>;

// =============================================================================
// BYK-141: Multi-Turn Refinement Types
// =============================================================================

export type RenderRefineCreate = z.infer<typeof RenderRefineCreateSchema>;
export type RenderWithRefinementsResponse = z.infer<typeof RenderWithRefinementsResponseSchema>;
export type RenderLineageResponse = z.infer<typeof RenderLineageResponseSchema>;
export { MAX_REFINEMENT_DEPTH };
